import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import {
  Users, Contact, Building2, ListChecks, CalendarCheck, Activity,
  BellRing, Target, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip,
} from "recharts";
import { toast } from "sonner";

function Progress({ value }) {
  return (
    <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden">
      <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(value, 100)}%` }} />
    </div>
  );
}

function AdminDashboard() {
  const [d, setD] = useState(null);
  const [kpiRows, setKpiRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setD(r.data));
    api.get("/kpi-targets").then((r) => {
      const rows = r.data.map((t) => {
        const metrics = Object.entries(t.metrics || {});
        const scored = metrics.filter(([, tgt]) => Number(tgt) > 0);
        const avg = scored.length
          ? Math.round((scored.reduce((s, [k, tgt]) => s + Math.min(Number(t.actuals?.[k] ?? 0) / Number(tgt), 1), 0) / scored.length) * 100)
          : 0;
        return { id: t.id, name: t.employee_name, month: t.month, achievement: avg };
      }).sort((a, b) => b.achievement - a.achievement);
      setKpiRows(rows);
    });
  }, []);
  if (!d) return <div className="h-40" />;

  const taskData = [
    { name: "Pending", value: d.tasks.pending, color: "hsl(45 100% 50%)" },
    { name: "In Progress", value: d.tasks.in_progress, color: "hsl(218 100% 33%)" },
    { name: "Completed", value: d.tasks.completed, color: "hsl(160 84% 39%)" },
  ];

  const sendReminders = async () => {
    const { data } = await api.post("/followups/send-reminders");
    toast.success(`Reminders sent to ${data.reminders_sent} employee(s)`);
  };

  return (
    <>
      <PageHeader
        title="Admin Overview"
        subtitle="Company-wide performance, customer allocation and weekly follow-up compliance."
        testId="admin-dashboard"
        actions={
          <Button onClick={sendReminders} className="rounded-sm gap-2 font-bold uppercase text-xs tracking-wide" data-testid="send-reminders-button">
            <BellRing className="h-4 w-4" /> Send Follow-up Reminders
          </Button>
        }
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Employees" value={d.employees} icon={Users} testId="stat-employees" />
        <StatCard label="Customers" value={d.clients} sub={`${d.active_clients} active`} icon={Contact} testId="stat-customers" />
        <StatCard label="Departments" value={d.departments} icon={Building2} />
        <StatCard label="Open Tasks" value={d.tasks.pending + d.tasks.in_progress} sub={`${d.tasks.completed} completed`} icon={ListChecks} tone="warning" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1 border border-border bg-card rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg tracking-tight">Weekly Follow-up</h3>
            <CalendarCheck className="h-5 w-5 text-primary" />
          </div>
          <p className="text-5xl font-display font-black tracking-tighter">
            {d.followup.compliance}<span className="text-2xl">%</span>
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {d.followup.done} of {d.followup.total} active customers followed up this week
          </p>
          <Progress value={d.followup.compliance} />
          <Button variant="outline" className="w-full mt-5 rounded-sm gap-2" onClick={() => nav("/compliance")} data-testid="view-compliance-button">
            View compliance <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="lg:col-span-2 border border-border bg-card rounded-sm p-6">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Customers per POC</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.poc_distribution} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
              <Tooltip cursor={{ fill: "hsl(210 16% 93%)" }} />
              <Bar dataKey="count" fill="hsl(218 100% 33%)" radius={[0, 2, 2, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Task Status</h3>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie data={taskData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                  {taskData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {taskData.map((t) => (
                <div key={t.name} className="flex items-center gap-2 text-sm">
                  <span className="h-3 w-3 rounded-sm" style={{ background: t.color }} />
                  <span className="text-muted-foreground">{t.name}</span>
                  <span className="font-bold ml-auto">{t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-display font-bold text-lg tracking-tight mb-4">Team by Department</h3>
          <div className="space-y-4">
            {d.dept_stats.map((s) => (
              <div key={s.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{s.name}</span>
                  <span className="text-muted-foreground">{s.employees} staff</span>
                </div>
                <Progress value={d.employees ? (s.employees / d.employees) * 100 : 0} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {kpiRows.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">Team KPI Performance</h3>
              <p className="text-sm text-muted-foreground">Actual achievement against monthly targets per employee.</p>
            </div>
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {kpiRows.map((r) => (
              <div key={r.id} data-testid={`kpi-emp-${r.id}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{r.name} <span className="text-muted-foreground text-xs">· {r.month}</span></span>
                  <span className="text-xs font-bold tabular-nums">{r.achievement}%</span>
                </div>
                <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm transition-all"
                    style={{ width: `${r.achievement}%`, background: r.achievement >= 80 ? "hsl(var(--success))" : r.achievement >= 40 ? "hsl(var(--primary))" : "hsl(var(--warning))" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </>
  );
}

function EmployeeDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/employee").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="h-40" />;

  const kpi = Object.entries(d.kpi_targets || {});
  const actuals = d.kpi_actuals || {};

  return (
    <>
      <PageHeader
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle={`${user.department} · ${user.designation} — your customers, tasks and this week's follow-ups.`}
        testId="employee-dashboard"
      />
      {d.followup.pending > 0 && (
        <div className="border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="followup-alert">
          <BellRing className="h-5 w-5 text-[hsl(45_100%_35%)] shrink-0" />
          <p className="text-sm font-medium">
            <span className="font-bold">{d.followup.pending}</span> customer(s) still need a follow-up this week.
          </p>
          <Button size="sm" variant="outline" className="ml-auto rounded-sm shrink-0" onClick={() => nav("/customers")}>
            Review now
          </Button>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="My Customers" value={d.my_clients} sub={`${d.active_clients} active`} icon={Contact} testId="stat-my-customers" />
        <StatCard label="Follow-ups Done" value={`${d.followup.done}/${d.followup.total}`} icon={CalendarCheck} tone="success" testId="stat-followups" />
        <StatCard label="Open Tasks" value={d.tasks.pending} sub={`${d.tasks.completed} completed`} icon={ListChecks} tone="warning" />
        <StatCard label="Assigned Tasks" value={d.tasks.assigned} icon={Activity} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card rounded-sm p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display font-bold text-lg tracking-tight">This Week's Follow-ups</h3>
            <CalendarCheck className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground mb-4">Week of {d.followup.week_start}</p>
          <p className="text-5xl font-display font-black tracking-tighter">
            {d.followup.total ? Math.round((d.followup.done / d.followup.total) * 100) : 100}<span className="text-2xl">%</span>
          </p>
          <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden mt-3">
            <div className="h-full bg-success" style={{ width: `${d.followup.total ? (d.followup.done / d.followup.total) * 100 : 100}%` }} />
          </div>
          <Button variant="outline" className="w-full mt-5 rounded-sm gap-2" onClick={() => nav("/customers")} data-testid="go-customers-button">
            Go to my customers <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="border border-border bg-card rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg tracking-tight">KPI Progress · {d.kpi_month}</h3>
            <Target className="h-5 w-5 text-primary" />
          </div>
          {kpi.length === 0 ? (
            <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center">
              No KPI targets set for you yet. Ask your admin to assign targets.
            </p>
          ) : (
            <div className="space-y-3.5 max-h-[360px] overflow-y-auto pr-1" data-testid="kpi-progress-list">
              {kpi.map(([k, target]) => {
                const actual = Number(actuals[k] ?? 0);
                const tgt = Number(target) || 0;
                const pct = tgt > 0 ? Math.round((actual / tgt) * 100) : (actual > 0 ? 100 : 0);
                const met = pct >= 100;
                return (
                  <div key={k} data-testid={`kpi-${k.replace(/[^a-zA-Z]+/g, "-")}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium line-clamp-1 pr-2">{k.replace(/#/g, "").trim()}</span>
                      <span className="text-xs font-bold shrink-0 tabular-nums">
                        <span className={met ? "text-success" : "text-foreground"}>{actual}</span>
                        <span className="text-muted-foreground"> / {tgt}</span>
                      </span>
                    </div>
                    <div className="h-2 w-full bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full transition-all rounded-sm"
                        style={{ width: `${Math.min(pct, 100)}%`, background: met ? "hsl(var(--success))" : pct >= 50 ? "hsl(var(--primary))" : "hsl(var(--warning))" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AdminDashboard /> : <EmployeeDashboard />;
}
